// apps/web/src/components/painel/home/popover-medicao.tsx — Fase 17.8, Plano 04 (D-06; UI-SPEC U-27).
// Botão de informação de um indicador: abre um Popover de 280px com o que mede, o período e a cobertura. Abre por
// clique, toque ou Enter (não só hover) e fecha com Esc (Radix). Só pesos 400/600 e tamanhos por token.
import type { ReactElement } from 'react';
import { Info } from 'lucide-react';
import type { Cobertura, Periodo } from '@pieltec/contracts';
import { TIMEZONE_OPERACIONAL } from '../../../lib/dia-operacional';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import '../../../styles/painel-home.css';

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'] as const;

const FORMATO_DIA_SP = new Intl.DateTimeFormat('en-CA', { timeZone: TIMEZONE_OPERACIONAL, year: 'numeric', month: '2-digit', day: '2-digit' });

/** Texto da unidade de contagem de cada critério de cobertura; critério desconhecido vira "itens". */
const TEXTO_CRITERIO: Readonly<Record<string, string>> = {
  'apontamentos-com-area': 'apontamentos com área cadastrada',
};

function partesDaData(dia: string): { readonly ano: number; readonly mes: number; readonly dia: number } | null {
  const [ano, mes, diaDoMes] = dia.split('-').map(Number);
  if (ano === undefined || mes === undefined || diaDoMes === undefined || !Number.isInteger(ano) || mes < 1 || mes > 12) return null;
  return { ano, mes, dia: diaDoMes };
}

function diaLongo(dia: string): string {
  const partes = partesDaData(dia);
  return partes === null ? dia : `${partes.dia} de ${MESES[partes.mes - 1]}`;
}

/** Período legível: "Hoje, 13 de outubro", "Últimos 14 dias úteis", "Outubro de 2026". `agora` só decide o "Hoje". */
export function descreverPeriodo(periodo: Periodo, agora: Date = new Date()): string {
  if (periodo.tipo === 'DIA') {
    const hoje = FORMATO_DIA_SP.format(agora);
    return periodo.inicio === hoje ? `Hoje, ${diaLongo(periodo.inicio)}` : diaLongo(periodo.inicio);
  }
  if (periodo.tipo === 'JANELA_DIAS') return periodo.dias === undefined ? 'Dias recentes' : `Últimos ${periodo.dias} dias`;
  if (periodo.tipo === 'DIAS_UTEIS') return periodo.dias === undefined ? 'Dias úteis recentes' : `Últimos ${periodo.dias} dias úteis`;
  const partes = partesDaData(periodo.inicio);
  if (partes === null) return periodo.inicio;
  const mes = MESES[partes.mes - 1] ?? '';
  return `${mes.charAt(0).toLocaleUpperCase('pt-BR')}${mes.slice(1)} de ${partes.ano}`;
}

/** "Base: 41 de 43 apontamentos com área cadastrada." */
export function textoCobertura(cobertura: Cobertura): string {
  return `Base: ${cobertura.base} de ${cobertura.total} ${TEXTO_CRITERIO[cobertura.criterio] ?? 'itens'}.`;
}

export interface ConteudoMedicaoProps {
  readonly definicao: string;
  readonly periodo: Periodo;
  readonly cobertura?: Cobertura;
  readonly agora?: Date;
}

const CLASSE_ROTULO = 'text-[length:var(--font-label)] font-semibold leading-[1.4] text-muted-foreground';
const CLASSE_TEXTO = 'min-w-0 break-words text-[length:var(--font-body)] font-normal leading-normal text-foreground';

/** Corpo do popover (separado para ser testável sem abrir o portal). */
export function ConteudoMedicao({ definicao, periodo, cobertura, agora }: ConteudoMedicaoProps): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        <p className={CLASSE_ROTULO}>O que mede</p>
        <p className={CLASSE_TEXTO}>{definicao}</p>
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <p className={CLASSE_ROTULO}>Período</p>
        <p className={CLASSE_TEXTO}>{descreverPeriodo(periodo, agora)}</p>
      </div>
      {cobertura !== undefined && (
        <div className="flex min-w-0 flex-col gap-1">
          <p className={CLASSE_ROTULO}>Cobertura</p>
          <p className={CLASSE_TEXTO}>{textoCobertura(cobertura)}</p>
        </div>
      )}
    </div>
  );
}

export interface PopoverMedicaoProps {
  readonly rotulo: string;
  readonly definicao: string;
  readonly periodo: Periodo;
  readonly cobertura?: Cobertura;
}

export function PopoverMedicao({ rotulo, definicao, periodo, cobertura }: PopoverMedicaoProps): ReactElement {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="painel-info" aria-label={`Como ${rotulo} é medido`} data-testid="painel-heroi-info">
          <Info className="size-4" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[280px] border-border bg-card p-4 text-foreground" data-testid="painel-medicao">
        <ConteudoMedicao definicao={definicao} periodo={periodo} cobertura={cobertura} />
      </PopoverContent>
    </Popover>
  );
}
